import { Alert, Button, Dialog, Drawer } from '@ui';
import { useId, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  submitLabel: string;
  onSubmit: () => void;
  pending?: boolean;
  /** General error (field errors are shown at the fields). */
  error?: string | undefined;
  size?: 'sm' | 'md' | 'lg';
  /** Side panel instead of a centered dialog (long forms). */
  drawer?: boolean;
  /** Extra buttons left of cancel, e.g. delete. */
  extraActions?: ReactNode;
  children: ReactNode;
}

/** A dialog (or drawer) around a form with cancel and submit buttons. */
export function FormDialog({
  open,
  onClose,
  title,
  description,
  submitLabel,
  onSubmit,
  pending = false,
  error,
  size = 'md',
  drawer = false,
  extraActions,
  children,
}: FormDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!pending) onSubmit();
  };
  const actions = (
    <>
      {extraActions}
      <Button variant="secondary" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" form={formId} loading={pending}>
        {submitLabel}
      </Button>
    </>
  );
  const body = (
    <form
      id={formId}
      onSubmit={submit}
      noValidate
      style={{ display: 'grid', gap: 'var(--space-4)' }}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      {children}
    </form>
  );
  if (drawer) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={title}
        description={description}
        closeLabel={t('common.close')}
        size={size === 'lg' ? 'lg' : 'md'}
        actions={actions}
      >
        {body}
      </Drawer>
    );
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      closeLabel={t('common.close')}
      size={size}
      actions={actions}
    >
      {body}
    </Dialog>
  );
}
